import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import {
    ApDropdown,
    ApDropdownItem,
} from '@uipath/portal-shell-react';
import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type {
    ByoLlmConfiguration,
    ByoLlmProbe,
} from '../../../../services/llm-gateway/LlmGateway.types';
import { ApiFlavor } from '../../../../services/llm-gateway/LlmGateway.types';
import {
    LabelWithTooltip,
    type TooltipProps,
} from '../../subcomponents/LabelWithTooltip';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';

const ApiFlavorDisplayNames: Record<ApiFlavor, string> = {
    [ApiFlavor.OpenAiChatCompletions]: 'OpenAI Chat Completions',
    [ApiFlavor.OpenAiResponses]: 'OpenAI Responses',
    [ApiFlavor.OpenAiEmbeddings]: 'OpenAI Embeddings',
    [ApiFlavor.GeminiGenerateContent]: 'Gemini Generate Content',
    [ApiFlavor.GeminiEmbeddings]: 'Gemini Embeddings',
    [ApiFlavor.AwsBedrockInvoke]: 'AWS Bedrock Invoke',
    [ApiFlavor.AwsBedrockConverse]: 'AWS Bedrock Converse',
    [ApiFlavor.AnthropicMessages]: 'Anthropic Messages',
};

interface LlmApiFlavorProps {
    value?: string;
    disabled?: boolean;
    availableApiFlavors?: ApiFlavor[];
    probes?: ByoLlmProbe[];
    llmConfigurationsField: ControllerRenderProps<AddEditConfigurationFormData, 'llmConfigurations'>;
    notifyFieldChanges: () => void;
    desiredModel: string;
    llmConfiguration: Partial<ByoLlmConfiguration>;
    labelTooltip?: TooltipProps;
    autoSelectFirst?: boolean;
}

export const LlmApiFlavorDropdown: React.FC<LlmApiFlavorProps> = ({
    value,
    disabled,
    availableApiFlavors,
    probes,
    llmConfigurationsField,
    notifyFieldChanges,
    desiredModel,
    llmConfiguration,
    labelTooltip,
    autoSelectFirst,
}) => {
    const { formatMessage: translate } = useIntl();
    const [ selectedValue, setSelectedValue ] = useState<string | undefined>(value);

    const formRef = useRef({
        llmConfigurationsField,
        llmConfiguration,
        notifyFieldChanges,
    });
    formRef.current = {
        llmConfigurationsField,
        llmConfiguration,
        notifyFieldChanges,
    };

    const displayedApiFlavors = useMemo(() => {
        let flavors: ApiFlavor[];

        if (probes?.length) {
            const probeFlavors = Array.from(new Set(probes.map(probe => probe.apiFlavor as ApiFlavor)));
            flavors = availableApiFlavors?.length
                ? probeFlavors.filter(flavor => availableApiFlavors.includes(flavor))
                : probeFlavors;
        } else {
            flavors = availableApiFlavors?.length ? [ ...availableApiFlavors ] : Object.values(ApiFlavor);
        }

        // Move OpenAiChatCompletions to the end
        const chatIdx = flavors.indexOf(ApiFlavor.OpenAiChatCompletions);
        if (chatIdx >= 0 && chatIdx !== flavors.length - 1) {
            flavors.splice(chatIdx, 1);
            flavors.push(ApiFlavor.OpenAiChatCompletions);
        }

        return flavors;
    }, [ probes, availableApiFlavors ]);

    useEffect(() => {
        setSelectedValue(value);
    }, [ value ]);

    useEffect(() => {
        if (disabled || !(autoSelectFirst || displayedApiFlavors.length === 1) || !desiredModel || displayedApiFlavors.length === 0) {
            return;
        }

        const {
            llmConfiguration: config,
            llmConfigurationsField: field,
            notifyFieldChanges: notify,
        } = formRef.current;

        if (value || config.apiFlavor) {
            return;
        }

        const firstFlavor = displayedApiFlavors[0];
        setSelectedValue(firstFlavor);

        field.onChange({
            ...field.value,
            [desiredModel]: {
                ...config,
                apiFlavor: firstFlavor,
            },
        });
        notify();
    }, [ autoSelectFirst, disabled, desiredModel, displayedApiFlavors, value ]);

    // Get warnings for the currently selected value
    return (
        <UiStack direction='column'>
            <LabelWithTooltip
                label={translate({ id: 'CLIENT_LLM_API_TYPE' })}
                required
                tooltip={labelTooltip ?? {
                    translationKey: 'CLIENT_BYO_LLM_TOOLTIP_API_TYPE',
                    placement: 'top',
                }}
                dataCy="llm-api-flavor-dropdown-label-with-tooltip"
            />
            <UiStack
                direction='row'
                align='center'
                gap={SpacingToken.XS}>
                <ApDropdown
                    style={{ width: '350px' }}
                    selectedValue={selectedValue}
                    onSelectedValueChanged={(event: CustomEvent) => {
                        const newValue = event.detail as ApiFlavor;
                        setSelectedValue(newValue);

                        const updatedConfiguration: Partial<ByoLlmConfiguration> = ({
                            ...(llmConfiguration),
                            apiFlavor: newValue,
                        });

                        llmConfigurationsField.onChange({
                            ...llmConfigurationsField.value,
                            [desiredModel]: updatedConfiguration,
                        });
                        notifyFieldChanges();
                    }}
                    disabled={disabled}
                    required
                    data-cy='llm-api-flavor-dropdown'>
                    {displayedApiFlavors.map(flavor => (
                        <ApDropdownItem
                            key={flavor}
                            value={flavor}
                            label={ApiFlavorDisplayNames[flavor] ?? flavor}
                        />
                    ))}
                </ApDropdown>
            </UiStack>
        </UiStack>
    );
};
